import React, { useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, CheckCheck, Maximize2, Play, Reply } from 'lucide-react';
import { MessengerAudioPlayer } from './MessengerAudioPlayer';
import { MessengerAvatar } from './messengerUtils';
import { MessengerMediaPreviewModal } from './MessengerMediaPreviewModal';
import { aggregateReactions, isEmojiOnlyMessage, summarizeMessageText } from './messengerThreadUtils';

// Small time formatter for the inline bubble timestamp — "14:07" style.
const formatBubbleTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(parsed);
};

export const MessengerMessageBubble = ({
  message,
  isMine,
  avatarName,
  avatarImage,
  dataTestId,
  children = null,
  showAvatar = true,
  showSenderLabel = false,
  groupPosition = 'single',
  spacingMode = 'guest',
  statusText = '',
  statusVariant = null,
  onReply,
  onLongPress,
  onDoubleTapHeart,
}) => {
  const messageType = message?.message_type || 'text';
  const metadata = message?.metadata || {};
  const hasText = Boolean(message?.content?.trim());
  const imageUrl = messageType === 'image' ? metadata.data_url : '';
  const videoUrl = messageType === 'video' ? metadata.data_url : '';
  const audioUrl = messageType === 'audio' ? metadata.data_url : '';
  const isEmojiOnly = messageType === 'text' && !imageUrl && !audioUrl && !videoUrl && isEmojiOnlyMessage(message?.content || '');
  const reactions = useMemo(() => aggregateReactions(metadata.reactions), [metadata.reactions]);
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [dragX, setDragX] = useState(0);
  const gestureRef = useRef({ startX: 0, startY: 0, moved: false, active: false, lock: 'idle' });
  const longPressTimeoutRef = useRef(null);
  const lastTapRef = useRef(0);

  const spacingConfig = useMemo(() => {
    if (spacingMode === 'host') {
      return { stackedGap: 4, separatedGap: 18, maxWidth: isMine ? '78%' : '86%' };
    }
    if (spacingMode === 'venue') {
      return { stackedGap: 4, separatedGap: 14, maxWidth: isMine ? '76%' : '84%' };
    }
    return { stackedGap: 4, separatedGap: 14, maxWidth: isMine ? '76%' : '85%' };
  }, [isMine, spacingMode]);

  // WhatsApp-style tail bubble — one flat corner near the sender's edge.
  const bubbleRadius = useMemo(() => {
    if (groupPosition === 'single') {
      return isMine ? '20px 20px 6px 20px' : '20px 20px 20px 6px';
    }
    if (isMine) {
      if (groupPosition === 'first') return '20px 20px 6px 20px';
      if (groupPosition === 'middle') return '20px 6px 6px 20px';
      return '20px 6px 20px 20px';
    }
    if (groupPosition === 'first') return '20px 20px 20px 6px';
    if (groupPosition === 'middle') return '6px 20px 20px 6px';
    return '6px 20px 20px 20px';
  }, [groupPosition, isMine]);

  const rowMarginBottom = groupPosition === 'first' || groupPosition === 'middle' ? spacingConfig.stackedGap : spacingConfig.separatedGap;
  const reactionBarVisible = reactions.length > 0;
  const mediaOnly = (imageUrl || audioUrl || videoUrl) && !hasText && !children;
  const replyPayload = metadata.reply_to;
  const bubbleTime = formatBubbleTime(message?.created_at);

  // Color language: outgoing bubbles use the brand accent as a solid tint
  // with white text for maximum contrast; incoming bubbles stay on the app
  // surface so quoted photos / audio waveforms remain crisp.
  const mineBg = 'linear-gradient(135deg, var(--accent-purple), var(--accent-purple-strong, #6335e0))';
  const mineTextColor = '#FFFFFF';
  const otherTextColor = 'var(--text)';
  const textColor = isMine && !isEmojiOnly && !mediaOnly ? mineTextColor : otherTextColor;
  const mineTimeColor = 'rgba(255,255,255,0.82)';
  const otherTimeColor = 'var(--text-2)';

  // Neutral grey drop-shadow for sender bubbles so the shadow reads as
  // depth, not as a second accent colour. Previously used the brand
  // purple at ~55% alpha, which bled a visible purple halo under every
  // outgoing bubble on non-purple palettes (Ocean / Forest / Sunset /
  // Allure red).
  const mineShadow = '0 10px 22px -14px rgba(17, 17, 26, 0.42), 0 2px 6px rgba(17, 17, 26, 0.12)';

  const clearLongPress = () => {
    window.clearTimeout(longPressTimeoutRef.current);
    longPressTimeoutRef.current = null;
  };

  const getClientX = (event) => {
    if (typeof event?.clientX === 'number') return event.clientX;
    if (event?.touches?.[0]?.clientX) return event.touches[0].clientX;
    if (event?.changedTouches?.[0]?.clientX) return event.changedTouches[0].clientX;
    return 0;
  };

  const getClientY = (event) => {
    if (typeof event?.clientY === 'number') return event.clientY;
    if (event?.touches?.[0]?.clientY) return event.touches[0].clientY;
    if (event?.changedTouches?.[0]?.clientY) return event.changedTouches[0].clientY;
    return 0;
  };

  const handleGestureStart = (event) => {
    gestureRef.current = {
      startX: getClientX(event),
      startY: getClientY(event),
      moved: false,
      active: true,
      lock: 'pending',
    };
    clearLongPress();
    if (onLongPress) {
      longPressTimeoutRef.current = window.setTimeout(() => {
        onLongPress(message, isMine);
      }, 550);
    }
  };

  const handleGestureMove = (event) => {
    if (!gestureRef.current.active) {
      return;
    }

    const currentX = getClientX(event);
    const currentY = getClientY(event);
    const deltaX = currentX - gestureRef.current.startX;
    const deltaY = currentY - gestureRef.current.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (gestureRef.current.lock === 'pending') {
      const directionMatches = isMine ? deltaX < 0 : deltaX > 0;

      if (absY > 10 && absY > absX) {
        gestureRef.current.lock = 'vertical';
        gestureRef.current.moved = true;
        clearLongPress();
        setDragX(0);
        return;
      }

      if (absX > 10 && absX > absY * 1.18) {
        gestureRef.current.moved = true;
        clearLongPress();

        if (!directionMatches) {
          gestureRef.current.lock = 'blocked';
          setDragX(0);
          return;
        }

        gestureRef.current.lock = 'horizontal';
      } else {
        return;
      }
    }

    if (gestureRef.current.lock === 'vertical' || gestureRef.current.lock === 'blocked') {
      gestureRef.current.moved = true;
      setDragX(0);
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    const allowedDelta = isMine ? Math.min(0, deltaX) : Math.max(0, deltaX);
    const easedDelta = allowedDelta * 0.82;
    setDragX(Math.max(-96, Math.min(96, easedDelta)));
  };

  const handleGestureEnd = () => {
    clearLongPress();
    const thresholdReached = gestureRef.current.lock === 'horizontal' && (isMine ? dragX <= -62 : dragX >= 62);
    if (thresholdReached && onReply) {
      onReply(message, isMine);
    }
    gestureRef.current = { startX: 0, startY: 0, moved: thresholdReached, active: false, lock: 'idle' };
    setDragX(0);
  };

  const handleClick = () => {
    if (gestureRef.current.moved || isMine || !onDoubleTapHeart) {
      return;
    }
    const now = Date.now();
    if (now - lastTapRef.current < 280) {
      onDoubleTapHeart(message);
      lastTapRef.current = 0;
      return;
    }
    lastTapRef.current = now;
  };

  const dragProgress = Math.min(1, Math.abs(dragX) / 62);

  const bubbleBackground = isEmojiOnly || mediaOnly
    ? 'transparent'
    : (isMine ? mineBg : 'var(--surface)');
  const bubbleBorder = isEmojiOnly || mediaOnly
    ? 'none'
    : (isMine ? '1px solid rgba(255,255,255,0.14)' : '1px solid var(--card-border)');
  const bubbleShadow = isEmojiOnly || mediaOnly
    ? 'none'
    : (isMine
      ? mineShadow
      : 'var(--premium-shadow-nested)');

  return (
    <>
      <div data-testid={dataTestId} style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginBottom: rowMarginBottom, position: 'relative' }}>
        <div data-testid={`${dataTestId}-reply-indicator`} style={{ position: 'absolute', top: '50%', [isMine ? 'right' : 'left']: 8, transform: `translateY(-50%) scale(${0.92 + (dragProgress * 0.08)})`, opacity: Math.min(1, dragProgress * 1.12), display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'none' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-surface)', border: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: dragProgress > 0 ? 'var(--premium-shadow-nested)' : 'none' }}>
            <Reply size={16} color="var(--accent-purple)" />
          </div>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', fontWeight: 700 }}>Reply</span>
        </div>

        <div
          style={{ display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8, maxWidth: '100%', transform: `translateX(${dragX}px)`, transition: longPressTimeoutRef.current ? 'none' : 'transform 170ms cubic-bezier(.22,1,.36,1)', touchAction: 'pan-y', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
          onMouseDown={handleGestureStart}
          onMouseMove={handleGestureMove}
          onMouseUp={handleGestureEnd}
          onMouseLeave={handleGestureEnd}
          onTouchStart={handleGestureStart}
          onTouchMove={handleGestureMove}
          onTouchEnd={handleGestureEnd}
          onTouchCancel={handleGestureEnd}
          onClick={handleClick}
          onContextMenu={(event) => {
            // Suppress the browser's native text-selection popup
            // ("Copy / Select all") that steals the long-press gesture
            // meant to open the message action sheet.
            event.preventDefault();
          }}
        >
          {showAvatar ? (
            <MessengerAvatar name={avatarName} imageUrl={avatarImage} size={30} textSize={12} dataTestId={`${dataTestId}-avatar`} />
          ) : (
            <div style={{ width: 30, height: 30, flexShrink: 0 }} />
          )}
          <div style={{ minWidth: 88, width: 'fit-content', maxWidth: spacingConfig.maxWidth }}>
            {showSenderLabel && !isMine ? (
              <p data-testid={`${dataTestId}-sender-label`} style={{ margin: '0 0 3px 6px', fontFamily: "'Inria Serif', serif", fontSize: 12, fontWeight: 700, color: 'var(--accent-purple)', letterSpacing: '0.01em' }}>
                {avatarName}
              </p>
            ) : null}
            <div
              style={{
                borderRadius: bubbleRadius,
                padding: isEmojiOnly ? 0 : mediaOnly ? 0 : '9px 12px 7px',
                background: bubbleBackground,
                border: bubbleBorder,
                boxShadow: bubbleShadow,
                transform: dragProgress > 0 ? `scale(${1 - (dragProgress * 0.012)})` : 'scale(1)',
                transition: 'transform 140ms ease, box-shadow 140ms ease',
                overflow: mediaOnly ? 'hidden' : 'visible',
                position: 'relative',
              }}
            >
              {replyPayload ? (
                <div data-testid={`${dataTestId}-reply-preview`} style={{ marginBottom: 8, borderRadius: 12, background: isMine ? 'rgba(255,255,255,0.14)' : 'var(--surface-2)', border: isMine ? '1px solid rgba(255,255,255,0.16)' : '1px solid var(--card-border)', padding: '6px 9px', borderLeft: `3px solid ${isMine ? 'rgba(255,255,255,0.72)' : 'var(--accent-purple)'}` }}>
                  <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 12, fontWeight: 700, color: isMine ? 'rgba(255,255,255,0.92)' : 'var(--text)' }}>{replyPayload.sender_name}</p>
                  <p style={{ margin: '2px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 12.5, color: isMine ? 'rgba(255,255,255,0.78)' : 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{replyPayload.preview_text}</p>
                </div>
              ) : null}

              {imageUrl ? (
                <button type="button" onClick={() => setIsMediaOpen(true)} data-testid={`${dataTestId}-image-button`} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'block', position: 'relative' }}>
                  <img src={imageUrl} alt="Shared image" data-testid={`${dataTestId}-image`} style={{ width: '100%', maxWidth: 260, maxHeight: 280, borderRadius: mediaOnly ? bubbleRadius : 16, display: 'block', objectFit: 'cover' }} />
                  <span style={{ position: 'absolute', right: 10, bottom: 10, width: 32, height: 32, borderRadius: '50%', background: 'rgba(17,17,26,0.56)', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }}>
                    <Maximize2 size={14} />
                  </span>
                </button>
              ) : null}

              {videoUrl ? (
                <div style={{ position: 'relative' }} data-testid={`${dataTestId}-video-container`}>
                  <video
                    src={videoUrl}
                    controls
                    playsInline
                    preload="metadata"
                    data-testid={`${dataTestId}-video`}
                    style={{ width: '100%', maxWidth: 260, maxHeight: 320, borderRadius: mediaOnly ? bubbleRadius : 16, display: 'block', background: '#000' }}
                  />
                  <button type="button" onClick={() => setIsMediaOpen(true)} data-testid={`${dataTestId}-video-expand-button`} style={{ position: 'absolute', right: 10, top: 10, width: 32, height: 32, borderRadius: '50%', background: 'rgba(17,17,26,0.56)', color: '#FFFFFF', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }}>
                    <Maximize2 size={14} />
                  </button>
                  <span style={{ position: 'absolute', left: 10, top: 10, padding: '3px 7px', borderRadius: 999, background: 'rgba(17,17,26,0.56)', color: '#FFFFFF', display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: "'Inria Serif', serif", fontSize: 11, fontWeight: 700, backdropFilter: 'blur(8px)' }}>
                    <Play size={10} fill="#FFFFFF" />
                    Video
                  </span>
                </div>
              ) : null}

              {audioUrl ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <MessengerAudioPlayer src={audioUrl} dataTestId={`${dataTestId}-audio`} compact />
                </div>
              ) : null}

              {hasText ? (
                <p
                  data-testid={`${dataTestId}-text`}
                  style={{
                    fontFamily: "'Inria Serif', serif",
                    fontSize: isEmojiOnly ? 52 : 15.5,
                    fontWeight: isEmojiOnly ? 500 : 500,
                    color: textColor,
                    lineHeight: isEmojiOnly ? 1 : 1.34,
                    margin: imageUrl || videoUrl || audioUrl || replyPayload ? '8px 0 0' : 0,
                    textAlign: 'left',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {message.content}
                </p>
              ) : null}

              {children ? <div style={{ marginTop: hasText || imageUrl || videoUrl || audioUrl ? 8 : 0 }}>{children}</div> : null}

              {/* Inline timestamp + ticks for text bubbles */}
              {!isEmojiOnly && !mediaOnly && (bubbleTime || statusVariant) ? (
                <div data-testid={`${dataTestId}-meta`} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, marginTop: 4, marginBottom: -2 }}>
                  {(message?.metadata || {}).edited ? (
                    <span data-testid={`${dataTestId}-edited`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 10.5, fontStyle: 'italic', color: isMine ? mineTimeColor : otherTimeColor, letterSpacing: '0.02em' }}>edited</span>
                  ) : null}
                  {bubbleTime ? (
                    <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 10.5, color: isMine ? mineTimeColor : otherTimeColor, letterSpacing: '0.02em' }}>{bubbleTime}</span>
                  ) : null}
                  {isMine && statusVariant === 'sent' ? (
                    <Check size={13} strokeWidth={2.6} color={mineTimeColor} data-testid={`${dataTestId}-tick-sent`} />
                  ) : null}
                  {isMine && statusVariant === 'delivered' ? (
                    <CheckCheck size={14} strokeWidth={2.6} color={mineTimeColor} data-testid={`${dataTestId}-tick-delivered`} />
                  ) : null}
                  {isMine && statusVariant === 'seen' ? (
                    <CheckCheck size={14} strokeWidth={2.6} color="#7BE0FF" data-testid={`${dataTestId}-tick-seen`} />
                  ) : null}
                </div>
              ) : null}

              {/* Overlay chip on media-only bubbles so photos/videos/voice
                  still show a timestamp + delivery/read tick without needing
                  a caption row underneath. */}
              {mediaOnly && (bubbleTime || statusVariant) ? (
                <div
                  data-testid={`${dataTestId}-meta`}
                  style={{
                    position: 'absolute',
                    right: 8,
                    bottom: 8,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px',
                    borderRadius: 999,
                    background: 'rgba(17,17,26,0.56)',
                    backdropFilter: 'blur(6px)',
                    color: '#FFFFFF',
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}
                >
                  {(message?.metadata || {}).edited ? (
                    <span data-testid={`${dataTestId}-edited`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 10.5, fontStyle: 'italic', color: '#FFFFFF', letterSpacing: '0.02em' }}>edited</span>
                  ) : null}
                  {bubbleTime ? (
                    <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 10.5, color: '#FFFFFF', letterSpacing: '0.02em' }}>{bubbleTime}</span>
                  ) : null}
                  {isMine && statusVariant === 'sent' ? (
                    <Check size={13} strokeWidth={2.6} color="#FFFFFF" data-testid={`${dataTestId}-tick-sent`} />
                  ) : null}
                  {isMine && statusVariant === 'delivered' ? (
                    <CheckCheck size={14} strokeWidth={2.6} color="#FFFFFF" data-testid={`${dataTestId}-tick-delivered`} />
                  ) : null}
                  {isMine && statusVariant === 'seen' ? (
                    <CheckCheck size={14} strokeWidth={2.6} color="#7BE0FF" data-testid={`${dataTestId}-tick-seen`} />
                  ) : null}
                </div>
              ) : null}
            </div>

            <AnimatePresence initial={false}>
              {reactionBarVisible ? (
                <motion.div
                  key={`${dataTestId}-reactions-bar`}
                  data-testid={`${dataTestId}-reactions`}
                  initial={{ opacity: 0, y: 8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.96 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', gap: 4, marginTop: -6, paddingInline: 8, flexWrap: 'wrap', alignItems: 'flex-end', position: 'relative', zIndex: 1 }}
                >
                  {reactions.map(({ emoji, count }) => (
                    <motion.div
                      key={`${dataTestId}-${emoji}`}
                      data-testid={`${dataTestId}-reaction-${emoji}`}
                      initial={{ opacity: 0, scale: 0.72, y: 6 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.72, y: 6 }}
                      transition={{ duration: 0.18, ease: 'easeOut' }}
                      style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, lineHeight: 1, background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 999, padding: '2px 6px' }}
                    >
                      <span style={{ fontSize: 16, lineHeight: 1 }}>{emoji}</span>
                      {count > 1 ? <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-2)', lineHeight: 1.1 }}>{count}</span> : null}
                    </motion.div>
                  ))}
                </motion.div>
              ) : null}
            </AnimatePresence>

            {statusText ? (
              <div data-testid={`${dataTestId}-status`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, marginTop: reactionBarVisible ? 2 : 4, paddingInline: 6 }}>
                <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 11.5, fontWeight: 700, color: 'var(--text-2)', letterSpacing: '0.01em' }}>{statusText}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <MessengerMediaPreviewModal open={isMediaOpen} onClose={() => setIsMediaOpen(false)} title={messageType === 'audio' ? 'Voice message' : messageType === 'video' ? 'Video' : 'Photo'} dataTestId={`${dataTestId}-media-preview`}>
        {imageUrl ? <img src={imageUrl} alt="Opened shared media" data-testid={`${dataTestId}-media-preview-image`} style={{ width: '100%', maxHeight: '72vh', objectFit: 'contain', borderRadius: 24, display: 'block', background: 'var(--surface-2)' }} /> : null}
        {videoUrl ? (
          <video src={videoUrl} controls playsInline autoPlay data-testid={`${dataTestId}-media-preview-video`} style={{ width: '100%', maxHeight: '72vh', objectFit: 'contain', borderRadius: 24, display: 'block', background: '#000' }} />
        ) : null}
        {audioUrl ? (
          <div style={{ padding: '8px 4px 2px' }}>
            <MessengerAudioPlayer src={audioUrl} dataTestId={`${dataTestId}-media-preview-audio`} compact={false} />
          </div>
        ) : null}
      </MessengerMediaPreviewModal>
    </>
  );
};
